import { z } from "zod";
import { RARETES } from "./carte-impression";
import { ETATS_RECOMPENSE, titreRecompenseSchema } from "./recompenses";

/**
 * Les contrats des boosters du TCG (ticket 6, plan `docs/plans/2026-11-01-castellan-tcg-boosters.md`) : les
 * sources des gains, les reglages (chances, brillance, Chatelain, gains) et ce que la regie et le site lisent.
 * Aucun identifiant n'est un nombre : un `bigint` de la base voyage en texte. Les dates restent en ISO 8601.
 */

/** Ce qui fait gagner une pochette. Les memes mots que la colonne `boosters_gains.source`. */
export const SOURCES_BOOSTER = [
  "presence",
  "follow",
  "sub",
  "sub_offert",
  "bits",
  "points",
  "recyclage",
  "don_regie",
] as const;
export type SourceBooster = (typeof SOURCES_BOOSTER)[number];

/** Les six raretes qu'un booster peut tirer : celles de `RARETES` sans le Chatelain, dans le meme ordre. */
export const RARETES_TIRABLES = [
  "ecuyer",
  "chevalier",
  "paladin",
  "heros",
  "seigneur",
  "legende",
] as const;
export type RareteTirable = (typeof RARETES_TIRABLES)[number];

/** Les chances d'un tirage, en dixiemes de pour cent : six raretes, des entiers, 1000 au total (decision 25). */
export const chancesSchema = z
  .strictObject({
    ecuyer: z.number().int().min(0).max(1000),
    chevalier: z.number().int().min(0).max(1000),
    paladin: z.number().int().min(0).max(1000),
    heros: z.number().int().min(0).max(1000),
    seigneur: z.number().int().min(0).max(1000),
    legende: z.number().int().min(0).max(1000),
  })
  .refine((chances) => Object.values(chances).reduce((somme, n) => somme + n, 0) === 1000, {
    message: "total",
  });
export type Chances = z.infer<typeof chancesSchema>;

export const reglagesBoostersSchema = z.strictObject({
  cartesParBooster: z.number().int().min(3).max(10),
  chances: chancesSchema,
  brillanteSur: z.number().int().min(2).max(1000),
  chatelainSur: z.number().int().min(1).max(100_000),
  doublonsParBooster: z.number().int().min(2).max(50),
  gains: z.strictObject({
    presence: z.number().int().min(0).max(5),
    follow: z.number().int().min(0).max(5),
    sub: z.number().int().min(0).max(5),
    subOffert: z.number().int().min(0).max(5),
    bitsParBooster: z.number().int().min(100).max(100_000),
  }),
});
export type ReglagesBoosters = z.infer<typeof reglagesBoostersSchema>;

/** L'origine d'une pochette, que le site met en mots : « Présence du 12 octobre », « Sub reçu », « Offert par le château : ... ». */
export const originePochetteSchema = z.object({
  source: z.enum(SOURCES_BOOSTER),
  survenueLe: z.string(),
  bits: z.number().int().nullable(),
  motif: z.string().nullable(),
});
export type OriginePochette = z.infer<typeof originePochetteSchema>;

/** Ce que la regie regle de la recompense « Booster du chateau » : titre, prix, etat, limite par viewer et par stream. */
export const reglerRecompenseSchema = z.strictObject({
  titre: titreRecompenseSchema,
  prix: z.number().int().min(1).max(1_000_000),
  active: z.boolean(),
  limiteParStream: z.number().int().min(1).max(100).nullable(),
});
export type ReglerRecompense = z.infer<typeof reglerRecompenseSchema>;

/** La recompense telle que la regie la lit : ses reglages, son etat, son identifiant chez Twitch, sa derniere synchro. */
export const recompenseSchema = reglerRecompenseSchema.extend({
  etat: z.enum(ETATS_RECOMPENSE),
  identifiant: z.string().nullable(),
  synchroniseeLe: z.string().nullable(),
});
export type Recompense = z.infer<typeof recompenseSchema>;

/** Ce que sont devenus les rachats : en cours, en echec (a traiter a la main chez Twitch), faits sur 30 jours. */
export const rachatsSchema = z.object({
  enCours: z.number().int(),
  echecs: z.number().int(),
  honores30j: z.number().int(),
  rembourses30j: z.number().int(),
});
export type Rachats = z.infer<typeof rachatsSchema>;

/** Les pochettes gagnees, l'ouverture et la reserve, vus de la regie. */
export const tableauBoostersSchema = z.object({
  reglages: reglagesBoostersSchema,
  recompense: recompenseSchema,
  rachats: rachatsSchema,
  totaux: z.object({
    /** Pochettes gagnees pendant le dernier stream (de son debut a sa fin, ou a maintenant s'il est ouvert). */
    dernierStream: z.number().int(),
    ouvertes: z.number().int(),
    scellees: z.number().int(),
  }),
  /** Pochettes gagnees par source sur les 30 derniers jours. */
  parSource: z.array(z.object({ source: z.enum(SOURCES_BOOSTER), pochettes: z.number().int() })),
  /** Les 20 derniers gains : le nom affiche de qui les a gagnes, jamais son login. */
  derniersGains: z.array(
    z.object({
      id: z.string(),
      nom: z.string(),
      source: z.enum(SOURCES_BOOSTER),
      nombre: z.number().int(),
      survenuLe: z.string(),
      motif: z.string().nullable(),
    }),
  ),
  /** Les 20 dernieres ouvertures : le nom affiche, la date, les raretes tirees dans l'ordre des positions. */
  dernieresOuvertures: z.array(
    z.object({
      id: z.string(),
      nom: z.string(),
      ouvertLe: z.string(),
      raretes: z.array(z.enum(RARETES)),
      brillantes: z.number().int(),
    }),
  ),
});
export type TableauBoosters = z.infer<typeof tableauBoostersSchema>;

/** `POST /api/tcg/boosters/offrir` : un don de la regie (decision 22). `cle` est tiree une fois par le formulaire. */
export const offrirBoostersCorpsSchema = z.strictObject({
  login: z.string().trim().min(1).max(40),
  nombre: z.number().int().min(1).max(10),
  motif: z.string().trim().min(1).max(80),
  cle: z.uuid(),
});
export type OffrirBoostersCorps = z.infer<typeof offrirBoostersCorpsSchema>;

export const offrirBoostersReponseSchema = z.object({ pochettes: z.number().int() });

/** `GET /api/tcg/boosters/simulation?n=1..10000` (decision 24). */
export const simulationQuerySchema = z.object({
  n: z.coerce.number().int().min(1).max(10_000).default(1000),
});
export type SimulationQuery = z.infer<typeof simulationQuerySchema>;

/**
 * Ce que rend la simulation : `n` boosters tires en memoire sur la reserve et les reglages du moment, sans rien ecrire
 * ni consommer. `parRarete` compte les cartes (le Chatelain a sa ligne), `chatelain` les boosters ou il est sorti,
 * `epuisements` les editions a tirage limite qui se sont videes (`apres` : le rang du booster qui a sorti la derniere).
 */
export const simulationSchema = z.object({
  n: z.number().int(),
  cartes: z.number().int(),
  parRarete: z.record(z.enum(RARETES), z.number().int()),
  brillantes: z.number().int(),
  chatelain: z.number().int(),
  replis: z.object({ descentes: z.number().int(), remontees: z.number().int() }),
  epuisements: z.array(
    z.object({ editionId: z.string(), nom: z.string(), apres: z.number().int() }),
  ),
  /** Les personnes tirables par rarete, au moment de la simulation. */
  reserve: z.record(z.enum(RARETES_TIRABLES), z.number().int()),
});
export type Simulation = z.infer<typeof simulationSchema>;
